import { FC } from 'react';
import { Divider, Flex, VStack } from '@chakra-ui/react';

import { formatAmount } from 'common/formatAmount';
import Button from 'components/_core/Button';
import SunshineCard from 'components/_core/SunshineCard';
import SunshineIcon from 'components/_core/SunshineIcon';
import Text from 'components/_core/Text';
import Tooltip from 'components/_core/Tooltip';
import BackButton from 'components/BackButton';
import ErrorCard from 'components/ErrorCard';
import Loader from 'components/Loader';
import { StepProps } from 'components/Stepper/types';
import TrackView from 'features/Analytics/TrackView';
import {
  BankTransferCreationStep,
  useBankTransferContext,
} from 'features/Payments/Pages/Transfers/contexts';
import * as ContentLayout from 'layouts/ContentLayout';
import ErrorLayout from 'layouts/ErrorLayout';
import * as Layout from 'layouts/StepperLayout';

import BankAccountsDetails from '../../components/BankAccountsDetails';
import useBankTransferData from '../../hooks/useBankTransferData';
import locales from './locales';

const ConfirmInternalDetails: FC<StepProps<BankTransferCreationStep>> = (
  props,
) => {
  const { bankTransfer } = useBankTransferContext('confirmInternalDetails');
  const { amount, fromBankAccountId, label, toBankAccountId } = bankTransfer;

  const {
    data,
    error,
    loading,
    refetch: refetchBankTranferData,
  } = useBankTransferData({
    fromBankAccountId,
    toBankAccountId,
  });

  if (error) {
    return (
      <ErrorLayout>
        <ErrorCard retry={refetchBankTranferData} />
      </ErrorLayout>
    );
  }

  if (loading || !data) {
    return <Loader />;
  }

  const { fromBankAccount, toBankAccount } = data;

  return (
    <Layout.Root>
      <Layout.Body>
        <TrackView pageName="ConfirmNewInternalTransferDetails" />

        <Layout.HeaderButtonsContainer>
          <BackButton onClick={props.goBack} />
        </Layout.HeaderButtonsContainer>

        <Layout.BodyScrollableSection>
          <ContentLayout.Header title={locales.checkDetails} />

          <VStack
            alignItems="stretch"
            paddingBottom="space-32"
            spacing="space-48"
            width="100%"
          >
            <BankAccountsDetails
              emittingBankAccount={fromBankAccount}
              receivingBankAccount={toBankAccount}
            />

            <VStack alignItems="stretch" spacing="space-8">
              <Text fontWeight="weight-500" size="lg" variant="primary">
                {locales.transferRecap}
              </Text>
              <SunshineCard.Group>
                <SunshineCard>
                  <SunshineCard.Content
                    flexDirection="row"
                    justifyContent="space-between"
                    spacing="space-24"
                  >
                    <Text>{locales.amount}</Text>
                    <Text fontWeight="weight-500" variant="primary">
                      {formatAmount({
                        forcePlusSign: false,
                        value: amount ?? 0,
                      })}
                    </Text>
                  </SunshineCard.Content>
                </SunshineCard>
                <SunshineCard>
                  <SunshineCard.Content
                    flexDirection="row"
                    justifyContent="space-between"
                    spacing="space-24"
                  >
                    <Text>{locales.fees}</Text>
                    <Flex flexDirection="row" gap="space-4">
                      <Tooltip description={locales.feeDetails}>
                        <SunshineIcon
                          color="grey.600"
                          marginTop="2px"
                          name="information"
                        />
                      </Tooltip>
                      <Text variant="primary">
                        {formatAmount({
                          forcePlusSign: false,
                          value: 0,
                        })}
                      </Text>
                    </Flex>
                  </SunshineCard.Content>
                </SunshineCard>
                <SunshineCard>
                  <SunshineCard.Content
                    flexDirection="row"
                    justifyContent="space-between"
                    spacing="space-24"
                    textAlign="right"
                  >
                    <Text>{locales.label}</Text>
                    {label ? (
                      <Text variant="primary">{label}</Text>
                    ) : (
                      <Text variant="disabled">{locales.noneLabel}</Text>
                    )}
                  </SunshineCard.Content>
                </SunshineCard>
                <SunshineCard>
                  <SunshineCard.Content
                    flexDirection="row"
                    justifyContent="space-between"
                    spacing="space-24"
                    textAlign="right"
                  >
                    <Text>{locales.transferType}</Text>
                    <Flex flexDirection="row" gap="space-4">
                      <Tooltip
                        description={locales.formatString(
                          locales.internalTransferTooltip,
                        )}
                      >
                        <SunshineIcon
                          color="grey.600"
                          marginTop="2px"
                          name="information"
                        />
                      </Tooltip>
                      <Text variant="primary">{locales.internalTransfer}</Text>
                    </Flex>
                  </SunshineCard.Content>
                </SunshineCard>
              </SunshineCard.Group>
            </VStack>
          </VStack>
        </Layout.BodyScrollableSection>

        <Layout.FooterContainer>
          <Divider />
          <Layout.FooterButtonsContainer>
            <Button
              icon="arrow-right"
              iconPosition="right"
              onClick={() => props.goNext()}
            >
              {locales.confirm}
            </Button>
          </Layout.FooterButtonsContainer>
        </Layout.FooterContainer>
      </Layout.Body>
    </Layout.Root>
  );
};

export default ConfirmInternalDetails;
